/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1692316800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Aug 18 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "tableau",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol",
      img: "img"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, NoCommonErrors} = _components;
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing view data from ", _jsx(_components.a, {
          href: "https://online.tableau.com",
          children: "Tableau Cloud\n"
        }), " or your own self-hosted Tableau Server instance. Most of the setup occurs in the Hightouch UI, but you need access to Tableau for your URL and credentials."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You need to allowlist Hightouch's IP addresses to let our systems contact your warehouse.\nReference our ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "networking docs"
          }), " to determine which IPs you need to allowlist.\nIf you're using Tableau Cloud, go to ", _jsx(_components.strong, {
            children: "Settings > Bridge"
          }), " and add them to your ", _jsx(_components.strong, {
            children: "Private Network Allowlist"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Tableau"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "Tableau"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports two authentication methods for Tableau: username and\npassword, and personal access token."
      }), "\n", _jsx(_components.p, {
        children: "For either method, you need to provide the following information:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "URL"
          }), ": The URL field should be the full homepage URL of your Tableau instance, for example, ", _jsx(_components.code, {
            children: "https://prod-useast-a.online.tableau.com/#/site/mycompanysite/home"
          }), ". You can copy and paste this from your browser's address bar."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For username and password authentication, you need to provide:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": The username of a user with access to the workbooks and views you want to sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the specified username."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For personal access token authentication, you need to provide:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Personal access token name"
          }), ": The name of your token, as configured in Tableau. This is not the token itself."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Personal access token secret"
          }), ": The secret for the token specified above."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["More info on personal access tokens can be found in the ", _jsx(_components.a, {
          href: "https://help.tableau.com/current/server/en-us/security_personal_access_tokens.htm",
          children: "Tableau\ndocumentation"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If you sign into Tableau using an authentication method such as Google, Salesforce or SAML, you need to use a ", _jsx(_components.a, {
            href: "https://help.tableau.com/current/server/en-us/security_personal_access_tokens.htm",
            children: "personal access token"
          }), " to authenticate the source."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Tableau as a source for the first time, Hightouch checks that you have the correct permissions. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Tableau."]
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In Hightouch, go to the ", _jsxs(_components.a, {
              href: "https://app.hightouch.com/models?from=docs",
              children: [_jsx(_components.strong, {
                children: "Models"
              }), " overview page"]
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Add model."
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Select the Tableau source you previously created."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select the ", _jsx(_components.strong, {
              children: "Workbook"
            }), " that contains the view you want to sync."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "sources/source-tableau-workbook-select.png",
              alt: "Tableau Workbook select"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select the ", _jsx(_components.strong, {
              children: "View"
            }), " you want to sync."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "sources/source-tableau-view-select.png",
              alt: "Tableau View select"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Optionally, set a custom ", _jsx(_components.strong, {
              children: "Max Data Age"
            }), ". Tableau caches each data export. If\nthere is a recent cache newer than the max data age, Tableau serves that data\ninstead of re-running the data export job. This can be useful if you want to\navoid re-running exports, but it can also lead to stale data. The default value\nused by Hightouch is 1 minute, which is the lowest value allowed by Tableau and\nmeans Hightouch uses the freshest data available."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "sources/source-tableau-max-data-age.png",
              alt: "Tableau Max Data Age input"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Preview your view.\n", _jsx(_components.img, {
              src: "sources/source-tableau-preview.png",
              alt: "Tableau View preview"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Continue"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Name your model and select its ", _jsx(_components.a, {
              href: "/models/creating-models#unique-primary-key-requirement",
              children: _jsx(_components.strong, {
                children: "primary key"
              })
            }), ". Hightouch uses the primary key to\ndetermine which rows have been added, changed, or removed since the last sync."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(NoCommonErrors, {
        source: "true"
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
